import React, { useState, useEffect } from 'react';

// Define level configurations: each level uses a different set (and count) of internal controls.
const levelConfigs = {
  easy: ["Segregation of Duties", "Authorization"],
  medium: ["Segregation of Duties", "Authorization", "Reconciliation", "Physical Controls"],
  hard: ["Segregation of Duties", "Authorization", "Reconciliation", "Physical Controls", "IT Controls", "Risk Assessment"]
};

const levelsOrder = ["easy", "medium", "hard"];

const AuditMemoryGame = ({ onBack }) => {
  const [level, setLevel] = useState("easy");
  const [cards, setCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [disableAll, setDisableAll] = useState(false);
  const [moves, setMoves] = useState(0);
  const [levelCompleted, setLevelCompleted] = useState(false);

  // Initialize the deck based on the current level.
  const initializeDeck = (currentLevel) => {
    const types = levelConfigs[currentLevel];
    const deck = [...types, ...types].map((type, index) => ({
      id: index,
      type,
      flipped: false,
      matched: false,
    }));
    // Shuffle using Fisher-Yates algorithm.
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
  };

  // Reset deck when level changes.
  useEffect(() => {
    setCards(initializeDeck(level));
    setFlippedIndices([]);
    setMoves(0);
    setLevelCompleted(false);
  }, [level]);

  // Check if the current level is complete.
  useEffect(() => {
    if (cards.length && cards.every((card) => card.matched)) {
      setLevelCompleted(true);
    }
  }, [cards]);

  const handleCardClick = (index) => {
    if (disableAll) return;
    if (cards[index].flipped || cards[index].matched) return;

    const newCards = [...cards];
    newCards[index].flipped = true;
    const newFlipped = [...flippedIndices, index];
    setCards(newCards);
    setFlippedIndices(newFlipped);

    if (newFlipped.length === 2) {
      setDisableAll(true);
      setMoves(moves + 1);
      setTimeout(() => {
        const [first, second] = newFlipped;
        const updatedCards = [...cards];
        if (updatedCards[first].type === updatedCards[second].type) {
          updatedCards[first].matched = true;
          updatedCards[second].matched = true;
        } else {
          updatedCards[first].flipped = false;
          updatedCards[second].flipped = false;
        }
        setCards(updatedCards);
        setFlippedIndices([]);
        setDisableAll(false);
      }, 1000);
    }
  };

  const handleNextLevel = () => {
    const currentIndex = levelsOrder.indexOf(level);
    if (currentIndex < levelsOrder.length - 1) {
      setLevel(levelsOrder[currentIndex + 1]);
    }
  };

  const resetGame = () => {
    setCards(initializeDeck(level));
    setFlippedIndices([]);
    setDisableAll(false);
    setMoves(0);
    setLevelCompleted(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow rounded-lg my-6">
      <h1 className="text-2xl font-bold text-center mb-4">Internal Control Memory Game</h1>
      <button onClick={onBack} className="mb-4 text-blue-500 hover:underline">
        &larr; Back to Menu
      </button>

      <div className="mb-4 text-center">
        <p className="font-semibold">Level: {level.charAt(0).toUpperCase() + level.slice(1)}</p>
        <p>Moves: {moves}</p>
      </div>

      {levelCompleted && (
        <div className="text-center mb-4">
          {level !== "hard" ? (
            <>
              <h2 className="text-xl font-bold mb-2">Level Completed!</h2>
              <button 
                onClick={handleNextLevel} 
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Next Level
              </button>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold mb-2">Congratulations! You've completed all levels!</h2>
              <button 
                onClick={resetGame} 
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Restart Game
              </button>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <div 
            key={card.id} 
            className="w-full h-32 md:h-40 cursor-pointer perspective" 
            onClick={() => handleCardClick(index)}
          >
            <div className={`card-inner relative w-full h-full transition-transform duration-500 ${card.flipped || card.matched ? 'rotate-y-180' : ''}`}>
              {/* Front Face: Displays "Internal Control" */}
              <div className="card-front absolute inset-0 bg-gray-300 rounded-lg flex items-center justify-center text-gray-700 text-sm md:text-lg font-bold backface-hidden">
                Internal Control
              </div>
              {/* Back Face: Displays the actual control type */}
              <div 
                className="card-back absolute inset-0 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm md:text-lg font-bold backface-hidden"
                style={{ transform: 'rotateY(180deg)' }}
              >
                {card.type}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditMemoryGame;
